import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent } from "@/components/ui/card";
import { Check, X, AlertCircle, FileWarning, Upload, FileText } from "lucide-react";
import { useBulkAppUrlImport } from "@/hooks/useBulkAppUrlImport";

interface BulkAppUrlImportDialogProps {
  onClose: () => void;
  onImportComplete?: () => void;
}

export const BulkAppUrlImportDialog = ({ onClose, onImportComplete }: BulkAppUrlImportDialogProps) => {
  const [csvContent, setCsvContent] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  
  const {
    importUrls,
    parseCsvContent,
    isImporting,
    progress,
    results,
    reset
  } = useBulkAppUrlImport();

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      setCsvContent(content);
      toast({
        title: "File loaded",
        description: `Loaded ${file.name}`,
      });
    };
    reader.onerror = () => {
      toast({
        title: "Error reading file",
        description: "Failed to read the CSV file",
        variant: "destructive",
      });
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (!csvContent.trim()) {
      toast({
        title: "Error",
        description: "Please upload a CSV file or paste content",
        variant: "destructive",
      });
      return;
    }

    // Validate we have parseable content
    const rows = parseCsvContent(csvContent);
    if (rows.length === 0) {
      toast({
        title: "Error",
        description: "No valid bundle ID and URL pairs found in the content",
        variant: "destructive",
      });
      return;
    }

    try {
      await importUrls(csvContent);
      if (onImportComplete) {
        onImportComplete();
      }
    } catch (error) {
      console.error('Import failed:', error);
    }
  };

  const handleClose = () => {
    reset();
    setCsvContent("");
    onClose();
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <Check className="w-4 h-4 text-green-600" />;
      case 'partial':
        return <AlertCircle className="w-4 h-4 text-yellow-600" />;
      case 'not_found':
        return <FileWarning className="w-4 h-4 text-orange-600" />;
      case 'error':
        return <X className="w-4 h-4 text-red-600" />;
      default:
        return <FileText className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusBg = (status: string) => {
    switch (status) {
      case 'success':
        return 'border-l-green-500';
      case 'partial':
        return 'border-l-yellow-500';
      case 'not_found':
        return 'border-l-orange-500';
      case 'error':
        return 'border-l-red-500';
      default:
        return 'border-l-gray-300';
    }
  };

  // Preview parsed data
  const previewRows = csvContent ? parseCsvContent(csvContent).slice(0, 3) : [];

  return (
    <DialogContent className="sm:max-w-[700px] max-h-[85vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>Import App URLs from CSV</DialogTitle>
        <DialogDescription>
          Upload a CSV file or paste content with bundle IDs and their webkit URLs.
        </DialogDescription>
      </DialogHeader>
      
      <div className="grid gap-4 py-4">
        {/* File Upload */}
        <div>
          <input
            type="file"
            accept=".csv,.txt"
            onChange={handleFileUpload}
            ref={fileInputRef}
            className="hidden"
          />
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={isImporting}
            className="w-full"
          >
            <Upload className="w-4 h-4 mr-2" />
            Upload CSV File
          </Button>
        </div>

        {/* Textarea for pasting */}
        <div>
          <label className="text-sm font-medium text-foreground mb-2 block">
            Or paste CSV content:
          </label>
          <Textarea
            value={csvContent}
            onChange={(e) => setCsvContent(e.target.value)}
            placeholder="com.spotify.client,music.spotify.com,open.spotify.com&#10;com.netflix.Netflix,netflix.com,assets.nflxext.com&#10;com.apple.Safari,apple.com,icloud.com"
            className="min-h-[150px] font-mono text-sm"
            disabled={isImporting}
          />
          <p className="text-xs text-muted-foreground mt-1">
            Format: <code className="bg-muted px-1 rounded">bundle.id,url1,url2,url3</code> (one app per line)
          </p>
        </div>

        {/* Preview */}
        {previewRows.length > 0 && !isImporting && results.length === 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-foreground">Preview ({previewRows.length} of {parseCsvContent(csvContent).length} rows)</h4>
            <div className="bg-muted rounded-md p-3 space-y-2">
              {previewRows.map((row, index) => (
                <div key={index} className="text-sm">
                  <span className="font-medium">{row.bundleId}</span>
                  <span className="text-muted-foreground ml-2">
                    → {row.urls.length} URL{row.urls.length > 1 ? 's' : ''}: {row.urls.slice(0, 2).join(', ')}
                    {row.urls.length > 2 && ` +${row.urls.length - 2} more`}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Progress */}
        {isImporting && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                Processing apps... ({progress.processed}/{progress.total})
              </span>
              <span className="text-sm text-muted-foreground">
                {Math.round((progress.processed / progress.total) * 100)}%
              </span>
            </div>
            <Progress value={(progress.processed / progress.total) * 100} />
          </div>
        )}

        {/* Results */}
        {results.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-foreground">Import Results</h4>
              <div className="flex gap-2 text-xs">
                <span className="text-green-600">
                  ✓ {results.filter(r => r.status === 'success').length}
                </span>
                <span className="text-yellow-600">
                  ⚠ {results.filter(r => r.status === 'partial').length}
                </span>
                <span className="text-orange-600">
                  ? {results.filter(r => r.status === 'not_found').length}
                </span>
                <span className="text-red-600">
                  ✗ {results.filter(r => r.status === 'error').length}
                </span>
              </div>
            </div>
            <div className="max-h-[300px] overflow-y-auto space-y-2">
              {results.map((result, index) => (
                <Card key={index} className={`p-0 border-l-4 ${getStatusBg(result.status)}`}>
                  <CardContent className="p-3">
                    <div className="flex items-center gap-3">
                      {getStatusIcon(result.status)}
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium truncate">
                            {result.appName || result.bundleId}
                          </span>
                          {result.appName && (
                            <span className="text-xs text-muted-foreground truncate">
                              {result.bundleId}
                            </span>
                          )}
                        </div>
                        
                        <p className="text-xs text-muted-foreground mt-1">
                          {result.message}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>
      
      <div className="flex justify-end space-x-2">
        <Button variant="outline" onClick={handleClose}>
          {isImporting ? "Cancel" : results.length > 0 ? "Done" : "Close"}
        </Button>
        {results.length === 0 && (
          <Button 
            onClick={handleImport}
            disabled={isImporting || !csvContent.trim()}
          >
            {isImporting ? "Importing..." : "Import URLs"}
          </Button>
        )}
      </div>
    </DialogContent>
  );
};
